import { useState } from 'react'
import { useDiscardDraft, usePreviewDraft, usePublishDraft } from '@/hooks/useWorkfrontMappings'
import { apiErrorMessage, apiErrorStatus } from '@/lib/api-error'
import { notification } from '@/notifications/notification-store'
import type { MappingDraftDTO, MappingPreviewDTO, MappingVersionDTO } from '@/services/workfront-mappings.service'
import { DraftBar } from './components/DraftBar'
import { PreviewSummary } from './components/PreviewSummary'
import { PublishDialog } from './components/PublishDialog'
import { useActorName } from './tab-support'

/** "Published version 13: 412 work orders re-mapping", the number only when a preview gave it. */
export const publishedMessage = (version: MappingVersionDTO, preview: MappingPreviewDTO | null): string =>
	preview
		? `Published version ${version.version}: ${preview.changedWorkOrders.toLocaleString('en-US')} ${preview.changedWorkOrders === 1 ? 'work order' : 'work orders'} re-mapping`
		: `Published version ${version.version}: the bundles are re-mapping`

/**
 * The draft bar and what its actions open: the preview, the publish dialog (which
 * computes the preview again, to show its numbers), and Discard. Commands, not
 * optimistic edits: each reports its outcome in the status bar. Stays mounted
 * while no draft is open, so a discard or publish that closes the draft still
 * reports its outcome.
 */
export function DraftControls({ draft }: { draft: MappingDraftDTO | null }) {
	const preview = usePreviewDraft()
	const publish = usePublishDraft()
	const discard = useDiscardDraft()
	const nameOf = useActorName()
	const [dialog, setDialog] = useState<'preview' | 'publish' | null>(null)

	const runPreview = () =>
		preview.mutate(undefined, {
			onError: (cause) => notification.error(apiErrorMessage(cause, 'Could not compute the preview.')),
		})
	const openPreview = () => {
		setDialog('preview')
		runPreview()
	}
	const openPublish = () => {
		setDialog('publish')
		if (!preview.data) runPreview()
	}
	const onPublish = (summary: string) =>
		publish.mutate(summary, {
			onSuccess: (version) => {
				notification.success(publishedMessage(version, preview.data ?? null))
				setDialog(null)
				preview.reset()
			},
			onError: (cause) =>
				notification.error(
					apiErrorStatus(cause) === 409
						? 'The draft had changed since it was loaded: review the reloaded draft and publish again.'
						: apiErrorMessage(cause, 'Could not publish the draft.'),
				),
		})
	const onDiscard = () =>
		discard.mutate(draft?.revision ?? 0, {
			onSuccess: () => notification.success('Draft discarded'),
			onError: (cause) => notification.error(apiErrorMessage(cause, 'Could not discard the draft.')),
		})

	return (
		<>
			{draft ? (
				<DraftBar
					draft={draft}
					openedByName={nameOf(draft.openedBy)}
					busy={publish.isPending || discard.isPending}
					onPreview={openPreview}
					onPublish={openPublish}
					onDiscard={onDiscard}
				/>
			) : null}
			<PreviewSummary
				open={dialog === 'preview'}
				preview={preview.isPending ? null : (preview.data ?? null)}
				failed={preview.isError}
				onRetry={runPreview}
				onClose={() => setDialog(null)}
				onPublish={() => setDialog('publish')}
				nameOf={nameOf}
			/>
			<PublishDialog
				open={dialog === 'publish'}
				preview={preview.isPending ? null : (preview.data ?? null)}
				previewFailed={preview.isError}
				submitting={publish.isPending}
				onCancel={() => setDialog(null)}
				onPublish={onPublish}
				nameOf={nameOf}
			/>
		</>
	)
}
